import { Card, CardActions, CardContent, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import reviews from '../data/etsy_reviews.json';

const Reviews = () => {
    const [visibleReviews, setVisibleReviews] = useState(6);
    const [shuffledReviews, setShuffledReviews] = useState([]);

    useEffect(() => {
        const getRandomBackground = (previousBackground, cardBackgrounds) => {
            let newBackground;
            do {
                newBackground = cardBackgrounds[Math.floor(Math.random() * cardBackgrounds.length)];
            } while (newBackground === previousBackground);
            return newBackground;
        };

        const shuffleArray = (array) => {
            return array.sort(() => 0.5 - Math.random());
        };

        const assignBackgroundsAndLengths = (reviews) => {
            const cardBackgrounds = ['#f8f9fa', '#dde7ec', '#fae3e3', '#fef3c7', '#d1d9d0', '#e3d7c3'];
            let previousBackground = null;
            const averageReviewLength = reviews.reduce((acc, review) => acc + review.reviewText.length, 0) / reviews.length;

            return reviews.map(review => {
                const background = getRandomBackground(previousBackground, cardBackgrounds);
                previousBackground = background;

                let reviewLength = 'average';
                if (review.reviewText.length < averageReviewLength * 0.75) {
                    reviewLength = 'short';
                } else if (review.reviewText.length > averageReviewLength * 2) {
                    reviewLength = 'long';
                }

                return { ...review, background, reviewLength };
            });
        };

        const shuffled = shuffleArray([...reviews]);
        setShuffledReviews(assignBackgroundsAndLengths(shuffled));
    }, []);

    const loadMoreReviews = () => {
        setVisibleReviews((prev) => prev + 6);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 50) {
                loadMoreReviews();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const groupReviews = (reviews) => {
        const longReviews = reviews.filter(review => review.reviewLength === 'long');
        const shortReviews = reviews.filter(review => review.reviewLength !== 'long');
        const groupedReviews = [];
        let alternate = true;

        while (longReviews.length > 0 || shortReviews.length > 0) {
            if (longReviews.length > 0 && shortReviews.length > 0) {
                if (alternate) {
                    groupedReviews.push([longReviews.shift(), shortReviews.shift()]);
                } else {
                    groupedReviews.push([shortReviews.shift(), longReviews.shift()]);
                }
                alternate = !alternate;
            } else if (shortReviews.length >= 3) {
                groupedReviews.push(shortReviews.splice(0, 3));
            } else if (shortReviews.length > 0) {
                groupedReviews.push(shortReviews.splice(0, shortReviews.length));
            }
        }

        // shuffle
        const mid = Math.floor(groupedReviews.length / 2);
        const firstHalf = groupedReviews.slice(0, mid);
        const secondHalf = groupedReviews.slice(mid);

        const shuffledGroupedReviews = [];
        while (firstHalf.length || secondHalf.length) {
            if (firstHalf.length) shuffledGroupedReviews.push(firstHalf.shift());
            if (secondHalf.length) shuffledGroupedReviews.push(secondHalf.shift());
        }

        return shuffledGroupedReviews.flat();
    };

    const groupedReviews = groupReviews(shuffledReviews);

    return (
        <Stack maxWidth="md" sx={{ mt: 4, mb: 4, gap: 3, mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
                What Our Customers Are Saying
            </Typography>
            <Typography variant="body1" align="center" paragraph>
                Discover the experiences of our satisfied customers.
            </Typography>
            <Grid container spacing={4}>
                {groupedReviews.slice(0, visibleReviews).map((review, index) => (
                    <Grid item xs={12} sm={6} md={review.reviewLength === 'long' ? 8 : 4} key={index} sx={{ opacity: 0, animation: 'fadeIn 1s forwards', animationDelay: `${index % 6 * 0.1}s` }}>
                        <Card sx={{ background: review.background, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <CardContent>
                                <Typography variant="body1" paragraph sx={{ fontSize: review.reviewLength === 'short' ? '1.5rem' : '1rem' }}>
                                    &quot;{review.reviewText}&quot;
                                </Typography>
                                <Typography variant="subtitle2">- {review.author || 'Anonymous'}</Typography>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {review.date}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Typography variant="caption" display="block" gutterBottom sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
                                    {review.listingTitle}
                                </Typography>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Stack>
    );
};

export default Reviews;
